import "./searchItem.css";

/**
 * Calculates the time remaining until a specified end date and time.
 * @param {string} endDateTimeString - The end date and time string in ISO format (e.g., "2024-04-02T20:00:00Z").
 * @returns {string} A string representing the time remaining in the format "X Day : Y Hours : Z Minutes remaining".
 */
function calculateTimeRemaining(endDateTimeString) {
  // Convert the end date and time string to a Date object
  const endDateTime = new Date(endDateTimeString);

  // Construct the current time in GMT
  const now = new Date(
    Date.UTC(
      new Date().getFullYear(),
      new Date().getMonth(),
      new Date().getDate(),
      new Date().getHours(),
      new Date().getMinutes(),
      new Date().getSeconds()
    )
  );

  // Get the time difference in milliseconds
  const timeDifference = endDateTime.getTime() - now.getTime();

  // Check if the end time has passed
  if (timeDifference <= 0) {
    return "Expired";
  }

  // Convert the time difference to days, hours, and minutes
  const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
  const hours = Math.floor(
    (timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );
  const minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60));

  // Format the time remaining string
  return `${days} Day : ${hours} Hours : ${minutes} Minutes remaining`;
}

/**
 * Component to display search item details.
 * @param {object} data - The data object containing details of the search item.
 * @returns {JSX.Element} A JSX element representing the search item.
 */
const SearchItem = ({ data }) => {
  return (
    <div className="searchItem">
      <div className="siProductImageContainer">
        <img
          src={data.competition_image_url}
          alt={data.competition_prize_name}
          className="siProductImage"
        />
      </div>
      <div className="siDescriptionContainer">
        <div className="siDescription">
          <div className="siHeatBoxAndEndDateContainer">
            <div className="siHeatBox">
              <span className="siHeat">
                &#x1F525; {data.competition_heat} &#x1F525;
              </span>
            </div>
            <div className="siEndDate">
              <i className="fa-regular fa-clock"></i> Draw -{" "}
              {data.competition_date_time}
            </div>
          </div>
          <div className="siTitle"> {data.competition_prize_name}</div>
          <div className="siPriceAndTimeRemainingContainer">
            <span className="siPrice">£{data.competition_price}</span>
            <span className="siDivider">|</span>
            <span className="siTimeRemaining">
              {calculateTimeRemaining(data.competition_date_time)}
              {/* ? Day : ?? Hours :?? Minutes remaining */}
            </span>
          </div>
          <div className="siStatsContainer">
            {data.competition_tickets_available !== null &&
            data.competition_tickets_sold !== null ? (
              <div className="siTicketContainer">
                <span className="siTickets">
                  <span className="siTicketsValue">
                    {data.competition_tickets_available}
                  </span>
                  Tickets
                </span>

                <span className="siTicketsSold">
                  <span className="siTicketsSoldValue">
                    {data.competition_tickets_sold}
                  </span>
                  Sold*
                </span>
              </div>
            ) : (
              <div className="siPercentageSold">
                <span className="siPercentageSoldValue">
                  {data.competition_percentage_sold}%
                </span>
                of Tickets Sold*
              </div>
            )}

            <span className="siChanceOfWin">
              <span className="siChanceOfWinValue">
                {data.odds_of_winning}%
              </span>
              Chance next ticket will win*
            </span>
            <span className="siCashAlternative">
              <span className="siCashAlternativeValue">
                £{data.competition_cash_value}
              </span>
              Cash Alternative
            </span>
          </div>
          <div className="siCompanyLogoAndButtonContainer">
            <img
              // src="./images/hotCompsLogo2.jpg"
              src={data.website_logo}
              // This runs as if it is in public folder.. so need to relative image as such
              alt={data.website_name}
              className="siCompanyLogoImage"
            />

            <a
              href={data.competition_play_url}
              target="_blank"
              rel="noopener noreferrer"
              className="playNowButton"
            >
              Play Now&nbsp;&nbsp;
              <i className="fa-solid fa-arrow-up-right-from-square"></i>
            </a>
          </div>
        </div>
        <div className="timeSinceLastRefresh">
          *last refreshed {data.competition_date_table_last_updated}
        </div>
      </div>
    </div>
  );
};

export default SearchItem;
